import { readableStreamFromArray } from '../../async/readableStreamFromArray';
import { type TransferPipeline, type SourceChunkInfo, createFileTarget } from '../../transfer/transferPipeline';
import { readWholeStream } from '../../util/readableStream';
import type { MxfSegment } from './rewrapPlanning';
import { mxfSegmentPartitionToKlvs, planMxfRewrap } from './rewrapPlanning';
import { klvToBytes } from './klv';
import { serializeRandomIndexPack } from './randomIndexPack';
import type { MxfRewrapInput, MxfRewrapItemProvider } from './rewrapInputs';
import { tsSegmentRangeToMxfRewrapInput } from './rewrapInputs';
import { fetchMxfRewrapDependencies } from './rewrapDependencies';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import type { ResourceAccessor, ResourceLocation } from '../../io/resource';
import type { Vfs } from '../../types/vfs';

export async function tsSegmentRangeMxfRewrapTransfer(
  services: {
    logger: StructuredLogger,
    mxfRewrapItemProvider: MxfRewrapItemProvider,
    resourceAccessor: ResourceAccessor,
    vfs: Vfs,
  },
  transferPipeline: TransferPipeline,
  itemId: string,
  startSegmentTime: number,
  endSegmentTime: number,
  targetFilename: string,
): Promise<void> {
  const { logger, vfs } = services;
  const input = await tsSegmentRangeToMxfRewrapInput(services, itemId, startSegmentTime, endSegmentTime);
  logger.log({ level: 'info', message: 'mxf rewrap input', input });
  const dependencies = await fetchMxfRewrapDependencies(services, [input]);
  const { segments } = planMxfRewrap(logger, dependencies);
  const chunkInfos = mxfSegmentsToChunkInfos(segments);
  const target = await createFileTarget(vfs, targetFilename);
  await transferPipeline.transfer({ chunkInfos, target, logger, progressId: targetFilename });
}

export async function makeMxfSubclip(
  services: {
    logger: StructuredLogger,
    resourceAccessor: ResourceAccessor,
    vfs: Vfs,
  },
  transferPipeline: TransferPipeline,
  info: {
    startFrameNumber: number,
    endFrameNumber: number,
    highRes: ResourceLocation,
    indexFileLocation: ResourceLocation,
    compatibilityHash: string | null,
    originFromTechnicalMetadata: number,
  },
  targetFilename: string,
): Promise<void> {
  const { logger, vfs } = services;
  const input : MxfRewrapInput = {
    highRes: info.highRes,
    index: info.indexFileLocation,
    inPoint: info.startFrameNumber,
    outPoint: info.endFrameNumber,
    compatibilityHash: info.compatibilityHash,
    origin: info.originFromTechnicalMetadata,
  };
  logger.log({ level: 'info', message: 'mxf rewrap input', input });
  const dependencies = await fetchMxfRewrapDependencies(services, [input]);
  const { segments } = planMxfRewrap(logger, dependencies);
  const chunkInfos = mxfSegmentsToChunkInfos(segments);
  const target = await createFileTarget(vfs, targetFilename);
  await transferPipeline.transfer({ chunkInfos, target, logger, progressId: targetFilename });
}

export function mxfSegmentsToChunkInfos(segments: MxfSegment[]): ReadableStream<SourceChunkInfo> {
  let targetIndex = 0;
  return readableStreamFromArray(segments)
    .pipeThrough(new TransformStream({
      async transform(segment: MxfSegment, controller) {
        switch (segment.type) {
          case 'essence': {
            controller.enqueue({
              resourceLocation: segment.highRes,
              start: segment.startByteOffset,
              end: segment.endByteOffset,
              totalNumberOfChunks: segments.length,
            });
            break;
          }
          case 'partition': {
            const klvs = mxfSegmentPartitionToKlvs(segment);
            const byteStream = readableStreamFromArray(klvs)
              .pipeThrough(klvToBytes());

            const bytes = await readWholeStream(byteStream);

            controller.enqueue({
              resourceLocation: { type: 'memory', bytes },
              start: 0,
              end: null,
              totalNumberOfChunks: segments.length,
            });
            break;
          }
          case 'random_index_pack': {
            const klv = serializeRandomIndexPack(segment.randomIndexPack);
            const byteStream = readableStreamFromArray([klv])
              .pipeThrough(klvToBytes());

            const bytes = await readWholeStream(byteStream);

            controller.enqueue({
              resourceLocation: { type: 'memory', bytes },
              start: 0,
              end: null,
              totalNumberOfChunks: segments.length,
            });
            break;
          }
        }
        targetIndex++;
      }
    }));
}
